import { supabase } from '../utils/supabase';
import { Organization } from '../types';

export interface UpdateOrganizationData {
  name: string;
  slogan?: string;
  logo_url?: string;
}

export interface UpdateOrganizationResponse {
  success: boolean;
  message: string;
  data?: Organization;
  error?: Error;
}

export interface CreateUserOrganizationData {
  userId: string;
  organizationName: string;
  organizationSlogan?: string;
  companySize: string;
}

export interface CreateUserOrganizationResponse {
  success: boolean;
  message: string;
  data?: Organization;
  error?: Error;
}

export class OrganizationService {
  private static readonly STORAGE_KEY = 'currentOrganization';

  /**
   * Get current organization from localStorage
   */
  static getCurrentOrganizationFromStorage(): Organization | null {
    try {
      const storedOrg = localStorage.getItem(this.STORAGE_KEY);
      return storedOrg ? JSON.parse(storedOrg) : null;
    } catch (error) {
      console.error('Error reading organization from storage:', error);
      return null;
    }
  }

  /**
   * Save current organization to localStorage
   */
  static saveCurrentOrganizationToStorage(organization: Organization): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(organization));
    } catch (error) {
      console.error('Error saving organization to storage:', error);
    }
  }

  /**
   * Clear current organization from localStorage
   */
  static clearCurrentOrganizationFromStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing organization from storage:', error);
    }
  }

  static async updateOrganization(
    organizationId: string,
    data: UpdateOrganizationData
  ): Promise<UpdateOrganizationResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    if (!data.name?.trim()) {
      return {
        success: false,
        message: 'Organization name is required',
        error: new Error('Organization name is required')
      };
    }

    try {
      const updateData: any = {
        name: data.name.trim(),
        slogan: data.slogan?.trim() || null
      };

      // Only update logo_url if it's provided in the data
      if (data.logo_url !== undefined) {
        updateData.logo_url = data.logo_url;
      }

      const { data: updatedOrg, error } = await supabase
        .from('organizations')
        .update(updateData)
        .eq('id', organizationId)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Organização atualizada com sucesso',
        data: updatedOrg
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  static async getOrganization(organizationId: string): Promise<Organization | null> {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Error fetching organization:', error);
      return null;
    }
  }

  static async createUserOrganization(
    data: CreateUserOrganizationData
  ): Promise<CreateUserOrganizationResponse> {
    if (!data.userId) {
      return {
        success: false,
        message: 'User ID is required',
        error: new Error('User ID is required')
      };
    }

    if (!data.organizationName?.trim()) {
      return {
        success: false,
        message: 'Organization name is required',
        error: new Error('Organization name is required')
      };
    }

    try {
      // Call the signup-user Edge Function for authenticated users
      const { data: result, error } = await supabase.functions.invoke('signup-user', {
        body: {
          userId: data.userId,
          organizationName: data.organizationName.trim(),
          organizationSlogan: data.organizationSlogan?.trim() || null,
          companySize: data.companySize
        }
      });

      if (error) {
        throw new Error(`Function error: ${error.message}`);
      }

      if (!result.success) {
        throw new Error(result.message || 'Unknown error from setup function');
      }

      return {
        success: true,
        message: 'Organização criada com sucesso',
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }
}
